import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, SignIn, SignOut, User, SignOut as ExitIcon } from "@phosphor-icons/react";

const ClockPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [clockStatus, setClockStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Live clock - update every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch clock status on mount
  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/timeclock/status`);
      setClockStatus(res.data);
    } catch (err) {
      console.error("Failed to fetch status:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/timeclock/clock-out`);
      if (res.data.clocked_out) {
        toast.success(`Clocked out! ${res.data.hours_worked}h worked.`);
        // Log out after clock-out
        await logout();
        navigate("/login");
      } else {
        toast.info("You weren't clocked in.");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (user?.role === "kds") {
        toast.info("Kitchen Display terminals don't clock in or out.");
      } else {
        toast.error(detail || "Couldn't clock out — please try again.");
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleClockIn = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/timeclock/clock-in`);
      if (res.data.clocked_in) {
        toast.success(res.data.already_open ? "You're already clocked in." : "Clocked in!");
        await fetchStatus();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (user?.role === "kds") {
        toast.info("Kitchen Display terminals don't clock in or out.");
      } else {
        toast.error(detail || "Couldn't clock in — please try again.");
      }
    } finally {
      setActionLoading(false);
    }
  };

  const formatTime = (d) => {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  const formatDate = (d) => {
    return d.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  };

  const clockInTime = clockStatus?.clock_in
    ? new Date(clockStatus.clock_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

  const elapsedMs = clockStatus?.clock_in
    ? currentTime.getTime() - new Date(clockStatus.clock_in).getTime()
    : 0;
  const elapsedHours = Math.floor(elapsedMs / 3600000);
  const elapsedMinutes = Math.floor((elapsedMs % 3600000) / 60000);
  const elapsedSeconds = Math.floor((elapsedMs % 60000) / 1000);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="clock-page">
      <div className="w-full max-w-md space-y-6">
        {/* Header — matches the auto-clock-in confirmation screen so the
            two flows feel consistent. Exit returns to login so a staffer
            can hand the terminal off after clocking in. */}
        <div className="flex items-center justify-between">
          <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center">
            <Clock size={28} weight="fill" className="text-white" />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { logout(); navigate("/login"); }}
            className="gap-1"
            data-testid="clock-exit-btn"
          >
            <ExitIcon size={14} /> Exit
          </Button>
        </div>

        {/* Current Time Display */}
        <div className="text-center">
          <p className="text-muted-foreground text-sm mb-1" data-testid="clock-date">
            {formatDate(currentTime)}
          </p>
          <p className="font-mono text-6xl font-light tracking-tight text-foreground" data-testid="clock-time">
            {formatTime(currentTime)}
          </p>
        </div>

        {/* User Card */}
        <Card className="border-2">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                <User size={28} className="text-primary" weight="bold" />
              </div>
              <div>
                <p className="font-heading text-xl font-medium" data-testid="clock-user-name">{user?.name}</p>
                <p className="text-sm text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </div>

            {/* Clock-in status */}
            {clockStatus?.clocked_in ? (
              <div className="space-y-4">
                {/* Use theme tokens so the panel flips correctly in dark mode
                    (was hard-coded to a light-green wash). */}
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4" data-testid="clocked-in-status">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
                    <span className="text-emerald-700 dark:text-emerald-300 font-medium text-sm">Clocked In</span>
                  </div>
                  <p className="text-emerald-700 dark:text-emerald-200/80 text-sm">
                    Since {clockInTime}
                  </p>
                  <div className="mt-3 bg-emerald-500/20 rounded-lg p-3 text-center">
                    <p className="text-xs text-emerald-700 dark:text-emerald-300 mb-1">Time Worked</p>
                    <p className="font-mono text-2xl font-medium text-emerald-800 dark:text-emerald-100" data-testid="elapsed-time">
                      {String(elapsedHours).padStart(2, "0")}:{String(elapsedMinutes).padStart(2, "0")}:{String(elapsedSeconds).padStart(2, "0")}
                    </p>
                  </div>
                </div>

                <Button
                  onClick={handleClockOut}
                  className="w-full h-16 text-lg gap-3 bg-red-600 hover:bg-red-700 text-white"
                  disabled={actionLoading}
                  data-testid="clock-out-btn"
                >
                  {actionLoading ? "Clocking out..." : <><SignOut size={24} weight="bold" /> Clock Out</>}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-muted border border-border rounded-xl p-4 text-center" data-testid="clocked-out-status">
                  <p className="text-muted-foreground text-sm">You are not clocked in</p>
                </div>
                <Button
                  onClick={handleClockIn}
                  className="w-full h-16 text-lg gap-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={actionLoading}
                  data-testid="clock-in-btn"
                >
                  {actionLoading ? "Clocking in..." : <><SignIn size={24} weight="bold" /> Clock In</>}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClockPage;
